import React, { useEffect, useRef, useState } from 'react'
import Container from '@embracesbs/component-container'
import Button2 from '@embracesbs/component-button2'
import TextField from '@embracesbs/component-textfield'
import TextFieldWrapper from '@embracesbs/component-textfieldwrapper'
import Checkbox from '@embracesbs/component-checkbox'
import Text from '@embracesbs/component-text'
import Icon from '@embracesbs/component-icon'
import Modal from '@embracesbs/component-modal'
import EmptyState from '@embracesbs/component-emptystate'
import DarkTabs from '../Components/Common/DarkTabs'
import { DEVELOPER_CENTER_PATH } from '../Components/MainMenu/navigation'
import LoadingState from '../Components/Common/LoadingState'
import { useApi } from '../Hooks'
import { downloadFile } from '../Utilities/helpers/helpers'

// Operator page for the tenant-configuration backfill (WI 165446).
//
// The API deliberately migrates ONE tenant per HTTP call, so the loop lives here:
// this page walks the selected tenants strictly sequentially, one awaited request
// at a time. That is not an optimisation detail - a tenant reads ~91 vault
// secrets and takes 5-10 seconds, so firing them concurrently would hammer the
// vault, and a mid-run failure has to leave the remaining tenants untouched and
// restartable rather than half-applied.
//
// SECURITY: the response carries key NAMES, reason tokens and counts only (SPEC
// NFR-5). Nothing here renders, stores or exports a configuration value, and the
// CSV/JSON export below is built exclusively from those counts and names.

interface Tenant {
  id: string
  tenantName: string
  displayName: string
  environment: string
}

// verify computes the diff and writes nothing; apply inserts the missing keys.
type MigrationMode = 'verify' | 'apply'

interface BackfillTotals {
  modelKeys: number
  presentInVault: number
  alreadyInSql: number
  inserted: number
  missingFromVault: number
  failed: number
}

interface KeyOutcome {
  key: string
  reason: string
}

// Mirrors tenants.api TenantConfigurationBackfillResponse.
interface BackfillResponse {
  tenantId: string
  tenantName: string
  mode: MigrationMode
  vaultReachable: boolean
  reason?: string
  totals: BackfillTotals
  insertedKeys: string[]
  missingFromVaultKeys: string[]
  skippedKeys: KeyOutcome[]
  failedKeys: KeyOutcome[]
  durationMs: number
}

// 'skipped' is a tenant the API could not read (no vault, vault unreachable) -
// deliberately NOT a failure: nothing was attempted, and the run carries on.
// 'stopped' is the tenant whose request the operator aborted with Stop - also not
// a failure, but unlike 'skipped' the request was in flight, so on an apply run the
// server may have written part of it; re-running the tenant settles it.
// 'failed' is a request that threw, or a tenant with per-key insert failures.
type RunOutcome = 'ok' | 'skipped' | 'stopped' | 'failed'

interface TenantRunResult {
  tenantId: string
  tenantName: string
  displayName: string
  environment: string
  mode: MigrationMode
  outcome: RunOutcome
  reason: string
  totals: BackfillTotals
  durationMs: number
  // Key NAMES behind the counts, so a row can answer "which ones?" without a
  // second run. Never values (SPEC NFR-5).
  insertedKeys: string[]
  missingFromVaultKeys: string[]
  failedKeys: KeyOutcome[]
}

interface BulkSelectMatch {
  input: string
  tenantId: string
  isExact: boolean
}

interface BulkSelectAmbiguity {
  input: string
  candidates: Tenant[]
}

interface BulkSelectResults {
  found: BulkSelectMatch[]
  ambiguous: BulkSelectAmbiguity[]
  notFound: string[]
}

const EMPTY_BULK_SELECT_RESULTS: BulkSelectResults = { found: [], ambiguous: [], notFound: [] }

const EMPTY_TOTALS: BackfillTotals = {
  modelKeys: 0,
  presentInVault: 0,
  alreadyInSql: 0,
  inserted: 0,
  missingFromVault: 0,
  failed: 0
}

const OUTCOME_COLOR: Record<RunOutcome, string> = {
  failed: 'var(--tma-danger-text)',
  skipped: 'var(--tma-warning-text)',
  stopped: 'var(--tma-text-muted)',
  ok: 'var(--tma-success-text)'
}

// The 'ok' outcome covers two states an operator reads very differently, so they
// are coloured apart: a tenant with keys to insert is WORK (blue, the app's action
// colour), while a tenant with nothing left to do is SETTLED (green). Colouring
// both green made a run that still has everything to migrate look finished.
const CHANGED_COLOR = 'var(--tma-accent)'
const UP_TO_DATE_COLOR = OUTCOME_COLOR.ok

const OUTCOME_ICON: Record<RunOutcome, string> = {
  failed: 'AlertTriangle',
  skipped: 'SubtractCircle',
  stopped: 'SubtractCircle',
  ok: 'CheckSquare'
}

// The app font. Raw spans on this page would otherwise fall back to the browser
// serif (index.css sets no body font); the design-system Text applies this
// itself, so setting it on the page root keeps everything consistent.
const APP_FONT = "'F-Default', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"

// Small tinted pill for counts and status labels, same shape as the rollout page.
const pill = (color: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  color,
  background: `color-mix(in srgb, ${color} 12%, transparent)`,
  borderRadius: '999px',
  padding: '2px 8px',
  fontSize: '12px',
  fontWeight: 600,
  whiteSpace: 'nowrap'
})

// ONE definition of the report's columns, used by both the header strip and every
// row. Grid tracks (not per-cell widths on flex children) are what keep them
// aligned: the browser sizes the tracks once for the container, so a cell cannot
// disagree with its header about where a column starts.
// Tenant | Outcome | To insert | Already in SQL | Failed keys | Missing in vault | Time
const REPORT_GRID = 'minmax(0, 1fr) 130px 92px 110px 92px 110px 76px'
const NUM_CELL: React.CSSProperties = { minWidth: 0, textAlign: 'right' }

const normalizeName = (value: string): string =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const findTenantMatches = (input: string, tenants: Tenant[]): { exact: Tenant[]; partial: Tenant[] } => {
  const needle = normalizeName(input)
  if (!needle) {
    return { exact: [], partial: [] }
  }

  const namesOf = (tenant: Tenant) => [normalizeName(tenant.tenantName), normalizeName(tenant.displayName)].filter(Boolean)

  const exact = tenants.filter((tenant) => namesOf(tenant).includes(needle))
  if (exact.length > 0) {
    return { exact, partial: [] }
  }

  const partial = tenants.filter((tenant) => namesOf(tenant).some((name) => name.includes(needle) || needle.includes(name)))
  return { exact: [], partial }
}

const formatDuration = (ms: number): string => {
  if (!isFinite(ms) || ms <= 0) return '0s'
  const seconds = Math.round(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${seconds % 60}s`
}

// The API returns a plain string body on failure (fetchApi parks it on error.data);
// fall back to the Error message so a network drop still reads sensibly in the report.
const errorMessage = (error: unknown): string => {
  const candidate = error as { data?: unknown; message?: string } | null
  if (candidate && typeof candidate.data === 'string' && candidate.data.trim().length > 0) {
    return candidate.data.trim()
  }
  if (candidate && candidate.data && typeof candidate.data === 'object') {
    return JSON.stringify(candidate.data)
  }
  return candidate?.message || 'Request failed'
}

// fetch rejects an aborted request with a DOMException named 'AbortError'. That is
// the operator pressing Stop, not a migration failure, so it is classified apart.
const isAbortError = (error: unknown): boolean => (error as { name?: string } | null)?.name === 'AbortError'

// Tenant display names are operator-controlled and land in this export, so a cell
// starting with = + - or @ would be evaluated as a formula when the CSV is opened
// in a spreadsheet. Prefixing a single apostrophe is the conventional escape: it
// forces the cell to be read as text and the apostrophe itself is not displayed.
const csvCell = (value: string | number): string => {
  const raw = `${value ?? ''}`
  const text = /^[=+\-@]/.test(raw) ? `'${raw}` : raw
  return /["\n\r,]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

const TenantConfigMigration: React.FC = () => {
  const { fetchApi } = useApi()

  const [segments, setSegments] = useState<string[]>([])
  const [selectedSegment, setSelectedSegment] = useState<string>('')
  const [isLoadingSegments, setIsLoadingSegments] = useState(true)
  const [isLoadingTenants, setIsLoadingTenants] = useState(false)
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [selectedTenants, setSelectedTenants] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState<string>('')

  const [mode, setMode] = useState<MigrationMode>('verify')
  const [view, setView] = useState<'tenants' | 'report'>('tenants')

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isBulkSelectModalOpen, setIsBulkSelectModalOpen] = useState(false)
  const [bulkSelectInput, setBulkSelectInput] = useState<string>('')
  const [bulkSelectResults, setBulkSelectResults] = useState<BulkSelectResults>(EMPTY_BULK_SELECT_RESULTS)
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null)

  // Run state. results is appended to as the loop walks, so the report fills in
  // live - an operator watching a 200-tenant run sees each verdict as it lands.
  const [isRunning, setIsRunning] = useState(false)
  const [isStopping, setIsStopping] = useState(false)
  const [runMode, setRunMode] = useState<MigrationMode>('verify')
  const [runTotal, setRunTotal] = useState(0)
  const [processed, setProcessed] = useState(0)
  const [currentTenantName, setCurrentTenantName] = useState<string>('')
  const [results, setResults] = useState<TenantRunResult[]>([])
  const [hasRun, setHasRun] = useState(false)
  const [wasStopped, setWasStopped] = useState(false)
  // Tenant ids whose key-name detail is open in the report.
  const [expandedRows, setExpandedRows] = useState<string[]>([])

  // A useState flag would be captured stale inside the running loop's closure, so
  // the loop reads its stop signal from a ref - the only value that is guaranteed
  // to be current on the iteration after the operator clicks Stop.
  const stopRequested = useRef(false)

  // Same reason as stopRequested: setIsRunning(true) is not visible until the next
  // render, so two clicks inside one tick would both pass an `isRunning` guard and
  // start two loops. The ref flips synchronously and is what the guard reads;
  // isRunning stays as the state that drives rendering.
  const isRunningRef = useRef(false)

  // The controller for the single request currently in flight, so Stop can abort it
  // rather than only preventing the next tenant from starting.
  const inFlightRequest = useRef<AbortController | null>(null)

  useEffect(() => {
    const fetchSegments = async () => {
      setIsLoadingSegments(true)
      try {
        const response = await fetchApi({ endpoint: '/api/featureflags/segments', method: 'GET' })
        if (Array.isArray(response) && response.length > 0) {
          setSegments(response)
          setSelectedSegment(response[0])
        } else {
          setSegments([])
        }
      } catch (error) {
        console.error('Error fetching segments:', error)
        setSegments([])
      } finally {
        setIsLoadingSegments(false)
      }
    }

    fetchSegments()
  }, [fetchApi])

  useEffect(() => {
    const fetchTenants = async () => {
      if (!selectedSegment) {
        setTenants([])
        return
      }

      setIsLoadingTenants(true)
      try {
        // The existing tenant endpoint is the single source of truth for "which
        // tenants exist"; it already filters out soft-deleted tenants server-side.
        const response = await fetchApi({
          endpoint: `/api/tenant/environment/${encodeURIComponent(selectedSegment)}`,
          method: 'GET'
        })
        setTenants(Array.isArray(response) ? response : [])
      } catch (error) {
        console.error('Error fetching tenants:', error)
        setTenants([])
      } finally {
        setIsLoadingTenants(false)
      }
    }

    fetchTenants()
  }, [selectedSegment, fetchApi])

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
    }
  }, [debounceTimer])

  const handleSegmentChange = (newSegment: string) => {
    // Switching stage mid-run would leave the progress panel describing tenants
    // that are no longer on screen; the run owns the page until it ends.
    if (isRunning || newSegment === selectedSegment) {
      return
    }
    setSelectedSegment(newSegment)
    setSelectedTenants([])
    setTenants([])
    setSearchQuery('')
    setIsLoadingTenants(true)
  }

  const toggleTenant = (tenantId: string) => {
    setSelectedTenants((prev) => (prev.includes(tenantId) ? prev.filter((id) => id !== tenantId) : [...prev, tenantId]))
  }

  const normalizedSearchQuery = normalizeName(searchQuery)
  const filteredTenants = tenants.filter(
    (tenant) =>
      normalizeName(tenant.displayName).includes(normalizedSearchQuery) || normalizeName(tenant.tenantName).includes(normalizedSearchQuery)
  )

  // Select-all works on what the search actually shows, so a filtered list is a
  // deliberate way to pick a subset rather than a trap that selects everything.
  const selectAllFiltered = () => {
    const visibleIds = filteredTenants.map((tenant) => tenant.id)
    setSelectedTenants((prev) => Array.from(new Set(prev.concat(visibleIds))))
  }

  const openBulkSelect = () => {
    setIsBulkSelectModalOpen(true)
    setBulkSelectInput('')
    setBulkSelectResults(EMPTY_BULK_SELECT_RESULTS)
  }

  const closeBulkSelect = () => {
    setIsBulkSelectModalOpen(false)
    setBulkSelectInput('')
    setBulkSelectResults(EMPTY_BULK_SELECT_RESULTS)
  }

  const processBulkSelection = (inputValue: string) => {
    if (!inputValue.trim()) {
      setBulkSelectResults(EMPTY_BULK_SELECT_RESULTS)
      return
    }

    const inputNames = inputValue
      .split(/[\n,]/)
      .map((name) => name.trim())
      .filter((name) => name.length > 0)

    const found: BulkSelectMatch[] = []
    const ambiguous: BulkSelectAmbiguity[] = []
    const notFound: string[] = []

    inputNames.forEach((inputName) => {
      const { exact, partial } = findTenantMatches(inputName, tenants)
      const matches = exact.length > 0 ? exact : partial

      if (matches.length === 1) {
        found.push({ input: inputName, tenantId: matches[0].id, isExact: exact.length > 0 })
      } else if (matches.length > 1) {
        ambiguous.push({ input: inputName, candidates: matches })
      } else {
        notFound.push(inputName)
      }
    })

    setBulkSelectResults({ found, ambiguous, notFound })
  }

  const confirmBulkSelection = () => {
    setSelectedTenants((prev) => Array.from(new Set(prev.concat(bulkSelectResults.found.map((match) => match.tenantId)))))
    closeBulkSelect()
  }

  const toResult = (tenant: Tenant, response: BackfillResponse, usedMode: MigrationMode): TenantRunResult => {
    const totals: BackfillTotals = { ...EMPTY_TOTALS, ...(response.totals || {}) }
    // Vault we could not read → skipped (nothing was attempted). Keys that failed
    // to insert → failed, even though the request itself returned 200.
    const outcome: RunOutcome = !response.vaultReachable ? 'skipped' : totals.failed > 0 ? 'failed' : 'ok'
    const reason = !response.vaultReachable
      ? response.reason || 'vault unreachable'
      : totals.failed > 0
        ? `${totals.failed} key${totals.failed !== 1 ? 's' : ''} could not be written`
        : ''

    return {
      tenantId: tenant.id,
      tenantName: tenant.tenantName,
      displayName: tenant.displayName,
      environment: tenant.environment || selectedSegment,
      mode: usedMode,
      outcome,
      reason,
      totals,
      durationMs: response.durationMs ?? 0,
      insertedKeys: response.insertedKeys ?? [],
      missingFromVaultKeys: response.missingFromVaultKeys ?? [],
      failedKeys: response.failedKeys ?? []
    }
  }

  const toFailure = (tenant: Tenant, usedMode: MigrationMode, reason: string, durationMs: number): TenantRunResult => ({
    tenantId: tenant.id,
    tenantName: tenant.tenantName,
    displayName: tenant.displayName,
    environment: tenant.environment || selectedSegment,
    mode: usedMode,
    outcome: 'failed',
    reason,
    totals: EMPTY_TOTALS,
    durationMs,
    insertedKeys: [],
    missingFromVaultKeys: [],
    failedKeys: []
  })

  const toStopped = (tenant: Tenant, usedMode: MigrationMode, durationMs: number): TenantRunResult => ({
    tenantId: tenant.id,
    tenantName: tenant.tenantName,
    displayName: tenant.displayName,
    environment: tenant.environment || selectedSegment,
    mode: usedMode,
    outcome: 'stopped',
    reason:
      usedMode === 'apply'
        ? 'stopped by the operator mid-request; re-run this tenant to confirm what was written'
        : 'stopped by the operator mid-request',
    totals: EMPTY_TOTALS,
    durationMs,
    insertedKeys: [],
    missingFromVaultKeys: [],
    failedKeys: []
  })

  // Strictly sequential: exactly one request in flight at any moment. A tenant
  // that throws is that tenant's failure only - the loop keeps going, so one dead
  // vault never costs the operator the rest of the run.
  const runMigration = async (usedMode: MigrationMode) => {
    const queue = tenants.filter((tenant) => selectedTenants.includes(tenant.id))
    if (isRunningRef.current || queue.length === 0) {
      return
    }

    isRunningRef.current = true
    stopRequested.current = false
    inFlightRequest.current = null
    setIsStopping(false)
    setWasStopped(false)
    setResults([])
    setProcessed(0)
    setRunTotal(queue.length)
    setRunMode(usedMode)
    setHasRun(true)
    setIsRunning(true)
    setView('report')

    let stoppedEarly = false

    try {
      for (let index = 0; index < queue.length; index++) {
        if (stopRequested.current) {
          stoppedEarly = true
          break
        }

        const tenant = queue[index]
        setCurrentTenantName(tenant.displayName || tenant.tenantName)
        const startedAt = Date.now()

        // One controller per request: Stop aborts whatever is in flight, so a vault
        // read that hangs cannot hold the run open.
        const controller = new AbortController()
        inFlightRequest.current = controller

        try {
          const response: BackfillResponse | undefined = await fetchApi({
            endpoint: `/api/migration/tenant-configuration/${encodeURIComponent(tenant.id)}?mode=${usedMode}`,
            method: 'POST',
            signal: controller.signal
          })

          if (response && typeof response === 'object') {
            setResults((prev) => prev.concat(toResult(tenant, response, usedMode)))
          } else {
            setResults((prev) => prev.concat(toFailure(tenant, usedMode, 'Empty response from the API', Date.now() - startedAt)))
          }
        } catch (error) {
          // An operator-initiated abort is not a migration failure - record the
          // tenant as stopped and leave the loop; the rest of the queue is untouched.
          if (isAbortError(error)) {
            stoppedEarly = true
            setResults((prev) => prev.concat(toStopped(tenant, usedMode, Date.now() - startedAt)))
            break
          }

          console.error('Migration failed for tenant', tenant.tenantName, error)
          setResults((prev) => prev.concat(toFailure(tenant, usedMode, errorMessage(error), Date.now() - startedAt)))
        } finally {
          // Runs before the break above takes effect, so an aborted tenant is still counted.
          inFlightRequest.current = null
          setProcessed((prev) => prev + 1)
        }
      }
    } finally {
      inFlightRequest.current = null
      isRunningRef.current = false
      setCurrentTenantName('')
      setIsRunning(false)
      setIsStopping(false)
      setWasStopped(stoppedEarly)
    }
  }

  const handleRun = () => {
    if (selectedTenants.length === 0 || isRunningRef.current) {
      return
    }
    // Verify writes nothing, so it runs straight away; a write always needs a
    // confirmation that names the tenants it is about to touch.
    if (mode === 'apply') {
      setIsConfirmModalOpen(true)
      return
    }
    runMigration('verify')
  }

  const handleStop = () => {
    stopRequested.current = true
    setIsStopping(true)
    // Stop the tenant that is running as well as the ones queued behind it: without
    // this, a vault read that never returns keeps the run alive indefinitely.
    inFlightRequest.current?.abort()
  }

  const selectedTenantDetails = tenants.filter((tenant) => selectedTenants.includes(tenant.id))
  const selectedSegmentLabel = selectedSegment ? selectedSegment.charAt(0).toUpperCase() + selectedSegment.slice(1) : ''
  const uniqueFoundTenantIds = Array.from(new Set(bulkSelectResults.found.map((match) => match.tenantId)))
  const hasAmbiguousNames = bulkSelectResults.ambiguous.length > 0
  const isApplyRun = runMode === 'apply'

  const failedResults = results.filter((result) => result.outcome === 'failed')
  const skippedResults = results.filter((result) => result.outcome === 'skipped')
  const stoppedResults = results.filter((result) => result.outcome === 'stopped')
  const okResults = results.filter((result) => result.outcome === 'ok')
  // In verify mode `inserted` means "would insert", so a changed tenant is one the
  // migration still has work to do on - the same split reads correctly in both modes.
  const changedResults = okResults.filter((result) => result.totals.inserted > 0)
  const unchangedResults = okResults.filter((result) => result.totals.inserted === 0)

  const totalInserted = results.reduce((sum, result) => sum + result.totals.inserted, 0)
  const totalMissingFromVault = results.reduce((sum, result) => sum + result.totals.missingFromVault, 0)
  const elapsedMs = results.reduce((sum, result) => sum + result.durationMs, 0)
  const averageMs = results.length > 0 ? elapsedMs / results.length : 0
  const remainingMs = Math.max(0, runTotal - processed) * averageMs
  const progressPercentage = runTotal > 0 ? Math.round((processed / runTotal) * 100) : 0

  // The completion signal the work item asks for: a verify pass over a finished
  // migration inserts nothing anywhere and skips nobody.
  const isFullyMigrated =
    hasRun &&
    !isRunning &&
    !wasStopped &&
    results.length > 0 &&
    failedResults.length === 0 &&
    skippedResults.length === 0 &&
    totalInserted === 0

  const reportRows = failedResults.concat(stoppedResults, skippedResults, changedResults, unchangedResults)

  const exportStamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
  const exportBaseName = `tenant-config-migration-${selectedSegment || 'all'}-${runMode}-${exportStamp}`

  const exportCsv = () => {
    const header = [
      'tenantName',
      'displayName',
      'tenantId',
      'environment',
      'mode',
      'outcome',
      'reason',
      'modelKeys',
      'presentInVault',
      'alreadyInSql',
      isApplyRun ? 'inserted' : 'toInsert',
      'missingFromVault',
      'failedKeys',
      'durationMs'
    ].join(',')

    const lines = reportRows.map((result) =>
      [
        result.tenantName,
        result.displayName,
        result.tenantId,
        result.environment,
        result.mode,
        result.outcome,
        result.reason,
        result.totals.modelKeys,
        result.totals.presentInVault,
        result.totals.alreadyInSql,
        result.totals.inserted,
        result.totals.missingFromVault,
        result.totals.failed,
        result.durationMs
      ]
        .map(csvCell)
        .join(',')
    )

    downloadFile(`${exportBaseName}.csv`, [header].concat(lines).join('\r\n'), 'text/csv;charset=utf-8')
  }

  const exportJson = () => {
    const report = {
      environment: selectedSegment,
      mode: runMode,
      generatedAt: new Date().toISOString(),
      stopped: wasStopped,
      totals: {
        tenantsProcessed: results.length,
        tenantsSelected: runTotal,
        failed: failedResults.length,
        skipped: skippedResults.length,
        changed: changedResults.length,
        unchanged: unchangedResults.length,
        keysInserted: totalInserted,
        keysMissingFromVault: totalMissingFromVault
      },
      tenants: reportRows
    }
    downloadFile(`${exportBaseName}.json`, JSON.stringify(report, null, 2), 'application/json;charset=utf-8')
  }

  const toggleRow = (tenantId: string) =>
    setExpandedRows((prev) => (prev.includes(tenantId) ? prev.filter((id) => id !== tenantId) : prev.concat(tenantId)))

  // Wrapped list of key names behind one of the counts. Names only, never values.
  const renderKeyList = (title: string, color: string, keys: string[]) => {
    if (keys.length === 0) {
      return null
    }

    return (
      <div style={{ marginBottom: '10px' }}>
        <Text textStyle='description' content={`${title} (${keys.length})`} style={{ display: 'block', marginBottom: '4px', color }} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {keys.map((key) => (
            <span
              key={key}
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                fontSize: '11px',
                background: 'var(--tma-panel-bg)',
                border: '1px solid var(--tma-list-card-border)',
                borderRadius: '4px',
                padding: '2px 6px',
                color: 'var(--tma-text-strong)'
              }}
            >
              {key}
            </span>
          ))}
        </div>
      </div>
    )
  }

  const renderResultRow = (result: TenantRunResult) => {
    // 'ok' splits by whether there is work: blue when keys are outstanding or were
    // written, green only when the tenant is genuinely settled.
    const color = result.outcome === 'ok' ? (result.totals.inserted > 0 ? CHANGED_COLOR : UP_TO_DATE_COLOR) : OUTCOME_COLOR[result.outcome]
    const okLabel = result.totals.inserted > 0 ? (isApplyRun ? 'Migrated' : 'To migrate') : 'Up to date'
    const label =
      result.outcome === 'ok' ? okLabel : result.outcome === 'skipped' ? 'Skipped' : result.outcome === 'stopped' ? 'Stopped' : 'Failed'

    const detailKeyCount = result.insertedKeys.length + result.missingFromVaultKeys.length + result.failedKeys.length
    const isExpanded = expandedRows.includes(result.tenantId)

    return (
      <div key={result.tenantId} style={{ borderBottom: '1px solid var(--tma-card-border)' }}>
        <div
          onClick={detailKeyCount > 0 ? () => toggleRow(result.tenantId) : undefined}
          style={{
            display: 'grid',
            gridTemplateColumns: REPORT_GRID,
            gap: '10px',
            alignItems: 'center',
            borderLeft: `3px solid ${color}`,
            padding: '8px 0 8px 10px',
            cursor: detailKeyCount > 0 ? 'pointer' : 'default'
          }}
        >
          <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
            {/* Only rows that actually have key names to show are expandable, so a
                chevron is never a dead affordance. */}
            {detailKeyCount > 0 ? (
              <Icon
                iconName={isExpanded ? 'ArrowDown1' : 'ArrowRight1'}
                size={12}
                color='var(--tma-text-muted)'
                style={{ flexShrink: 0 }}
              />
            ) : (
              <span style={{ width: '12px', flexShrink: 0 }} />
            )}
            <div style={{ minWidth: 0 }}>
              <Text textStyle='body' content={result.displayName || result.tenantName} singleLine hasTooltipEllipsis />
              <Text
                textStyle='description'
                textColor='grey'
                content={result.reason ? `${result.tenantName} · ${result.reason}` : result.tenantName}
                singleLine
                hasTooltipEllipsis
                style={{ display: 'block' }}
              />
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            <span style={pill(color)}>
              <Icon iconName={OUTCOME_ICON[result.outcome]} size={14} color={color} />
              {label}
            </span>
          </div>
          <Text textStyle='body' content={`${result.totals.inserted}`} style={NUM_CELL} />
          <Text textStyle='body' content={`${result.totals.alreadyInSql}`} style={NUM_CELL} />
          <Text
            textStyle='body'
            content={`${result.totals.failed}`}
            style={{ ...NUM_CELL, color: result.totals.failed > 0 ? OUTCOME_COLOR.failed : undefined }}
          />
          <Text
            textStyle='body'
            content={`${result.totals.missingFromVault}`}
            style={{ ...NUM_CELL, color: result.totals.missingFromVault > 0 ? OUTCOME_COLOR.skipped : undefined }}
          />
          <Text textStyle='description' textColor='grey' content={formatDuration(result.durationMs)} style={NUM_CELL} />
        </div>

        {isExpanded ? (
          <div style={{ background: 'var(--tma-card-bg)', borderLeft: `3px solid ${color}`, padding: '10px 12px 6px 28px' }}>
            {renderKeyList(isApplyRun ? 'Inserted into SQL' : 'Would be inserted', CHANGED_COLOR, result.insertedKeys)}
            {renderKeyList('Never set in this vault (nothing to copy)', OUTCOME_COLOR.skipped, result.missingFromVaultKeys)}
            {renderKeyList(
              'Failed to write',
              OUTCOME_COLOR.failed,
              result.failedKeys.map((failure) => `${failure.key} (${failure.reason})`)
            )}
          </div>
        ) : null}
      </div>
    )
  }

  const renderResultSection = (title: string, color: string, sectionResults: TenantRunResult[], note: string) => {
    if (sectionResults.length === 0) {
      return null
    }

    return (
      <div style={{ marginBottom: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <Text textStyle='h6' content={title} style={{ fontSize: '14px' }} />
          <span style={pill(color)}>{sectionResults.length}</span>
          <Text textStyle='description' textColor='grey' content={note} />
        </div>
        {/* Header and rows are driven by the SAME grid template (REPORT_GRID) and the
            same left padding. They previously used different layout engines - a flex
            div here, the design system's TableRow below - so the columns drifted out
            of alignment and the tenant cell was squeezed while the row's right edge
            held dead space. Keep these two in lockstep. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: REPORT_GRID,
            gap: '10px',
            alignItems: 'center',
            padding: '0 0 4px 13px',
            borderBottom: '1px solid var(--tma-list-card-border)'
          }}
        >
          <Text textStyle='description' textColor='grey' content='Tenant' style={{ minWidth: 0 }} />
          <Text textStyle='description' textColor='grey' content='Outcome' style={{ minWidth: 0 }} />
          <Text textStyle='description' textColor='grey' content={isApplyRun ? 'Inserted' : 'To insert'} style={NUM_CELL} />
          <Text textStyle='description' textColor='grey' content='Already in SQL' style={NUM_CELL} />
          <Text textStyle='description' textColor='grey' content='Failed keys' style={NUM_CELL} />
          {/* NOT a problem indicator: it counts model keys this tenant's vault never
              held, so there is nothing for the backfill to copy. A tenant can be
              fully "Up to date" and still have a non-zero count here - the two
              measure different things (SQL vs vault, and vault vs model). */}
          <Text textStyle='description' textColor='grey' content='Not set in vault' style={NUM_CELL} />
          <Text textStyle='description' textColor='grey' content='Time' style={NUM_CELL} />
        </div>
        <div>{sectionResults.map(renderResultRow)}</div>
      </div>
    )
  }

  if (isLoadingSegments) {
    return (
      <div style={{ height: 'var(--app-height, 100vh)' }}>
        <LoadingState text='Loading environments…' />
      </div>
    )
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'row', height: 'var(--app-height, 100vh)', overflow: 'hidden', fontFamily: APP_FONT }}>
        {/* Left column: stage tabs, selection tools and the mode switch. */}
        <div style={{ width: '30%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <DarkTabs
            backTo={DEVELOPER_CENTER_PATH}
            tabs={segments.map((segment) => ({ id: segment, text: segment.charAt(0).toUpperCase() + segment.slice(1) }))}
            activeId={selectedSegment}
            onTabClick={handleSegmentChange}
          />

          <div
            style={{
              flexShrink: 0,
              background: 'var(--tma-panel-bg)',
              borderBottom: '1px solid var(--tma-list-card-border)',
              padding: '10px'
            }}
          >
            <div className='search-no-glow' style={{ marginBottom: '8px' }}>
              <TextField
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder='Search tenants'
                type='text'
                variant='default'
                isSearchMode
                onClear={() => setSearchQuery('')}
              />
            </div>
            {/* Grid, not flex: Button2's isFullWidth sets width:100% on the button
                itself, which in a flex row resolves against the ROW (not an equal
                share of it), so two side-by-side full-width buttons each take the
                full width and the second one is pushed outside the panel's
                overflow:hidden and clipped. Grid tracks give each button its own
                1fr cell, so width:100% means "fill my cell". */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
              <Button2
                text={`Select all (${filteredTenants.length})`}
                onClick={selectAllFiltered}
                isDisabled={isRunning || filteredTenants.length === 0}
                variant='outlined'
                type='button'
                buttonSize='30px'
                buttonRadius='8px'
                isFullWidth
                style={{ justifyContent: 'center' }}
              />
              <Button2
                text='Clear'
                onClick={() => setSelectedTenants([])}
                isDisabled={isRunning || selectedTenants.length === 0}
                variant='outlined'
                type='button'
                buttonSize='30px'
                buttonRadius='8px'
                isFullWidth
                style={{ justifyContent: 'center' }}
              />
            </div>
            <div style={{ marginTop: '6px' }}>
              <Button2
                text='Bulk select by name'
                onClick={openBulkSelect}
                isDisabled={isRunning}
                variant='outlined'
                type='button'
                buttonSize='30px'
                buttonRadius='8px'
                isFullWidth
                style={{ justifyContent: 'center' }}
              />
            </div>
          </div>

          {/* Mode switch. Verify is the default and writes nothing; the banner spells
              out the difference so a write is never a surprise. */}
          <div
            style={{
              flexShrink: 0,
              background: 'var(--tma-panel-bg)',
              borderBottom: '1px solid var(--tma-list-card-border)',
              padding: '12px 10px'
            }}
          >
            <Text textStyle='description' textColor='grey' content='Mode' style={{ display: 'block', marginBottom: '6px' }} />
            {/* Grid for the same reason as the selection row above - as a flex row the
                Migrate button was clipped to 4 visible pixels, which made the mode
                switch look like a single Verify button with no way to reach Migrate. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
              <Button2
                text='Verify'
                leftElement={<Icon iconName='Search' size={14} color={mode === 'verify' ? '#ffffff' : 'var(--tma-accent)'} />}
                onClick={() => setMode('verify')}
                isDisabled={isRunning}
                variant={mode === 'verify' ? 'contained' : 'outlined'}
                type='button'
                buttonSize='30px'
                buttonRadius='8px'
                isFullWidth
                style={{ justifyContent: 'center' }}
              />
              <Button2
                text='Migrate'
                leftElement={<Icon iconName='Swap' size={14} color={mode === 'apply' ? '#ffffff' : 'var(--tma-danger-text)'} />}
                onClick={() => setMode('apply')}
                isDisabled={isRunning}
                variant={mode === 'apply' ? 'contained' : 'outlined'}
                isDanger={mode === 'apply'}
                type='button'
                buttonSize='30px'
                buttonRadius='8px'
                isFullWidth
                style={{ justifyContent: 'center' }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'flex-start',
                marginTop: '10px',
                borderStyle: 'solid',
                borderWidth: '1px',
                borderColor:
                  mode === 'apply' ? 'var(--tma-danger-border)' : 'color-mix(in srgb, var(--tma-accent) 35%, var(--tma-panel-bg))',
                background: mode === 'apply' ? 'var(--tma-danger-tint)' : 'color-mix(in srgb, var(--tma-accent) 10%, var(--tma-panel-bg))',
                borderRadius: '8px',
                padding: '10px 12px'
              }}
            >
              <Icon
                iconName={mode === 'apply' ? 'AlertTriangle' : 'Search'}
                size={16}
                color={mode === 'apply' ? 'var(--tma-danger-text)' : 'var(--tma-accent)'}
                style={{ flexShrink: 0, marginTop: '1px' }}
              />
              <Text
                textStyle='description'
                content={
                  mode === 'apply'
                    ? 'Migrate WRITES: every key the vault has and SQL is missing is inserted. Existing rows are never touched, and re-running is safe.'
                    : 'Verify writes nothing. It reports, per tenant, exactly which keys a migration would insert.'
                }
                style={{ color: mode === 'apply' ? 'var(--tma-danger-text)' : 'var(--tma-accent)' }}
              />
            </div>
          </div>

          <div
            style={{ flex: 1, minHeight: 0, background: 'var(--tma-page-bg)', padding: '12px 10px', overflowY: 'auto' }}
            className='custom-scrollbar'
          >
            <Text
              textStyle='description'
              textColor='grey'
              content={`${selectedTenants.length} of ${tenants.length} tenants selected`}
              style={{ display: 'block' }}
            />
            <Text
              textStyle='description'
              textColor='grey'
              content={`One request per tenant, run one at a time. Reckon on 5-10 seconds each, roughly ${formatDuration(
                selectedTenants.length * 7500
              )} for this selection.`}
              style={{ display: 'block', marginTop: '8px' }}
            />
          </div>
        </div>

        {/* Right column: the 52px action band, then either the tenant grid or the report. */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            background: 'var(--tma-panel-bg)',
            padding: '0 20px',
            boxSizing: 'border-box'
          }}
        >
          <div style={{ flexShrink: 0, height: '52px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Button2
                  text='Select tenants'
                  onClick={() => setView('tenants')}
                  variant={view === 'tenants' ? 'contained' : 'subtle'}
                  type='button'
                  buttonSize='30px'
                  buttonRadius='8px'
                />
                <Button2
                  text={hasRun ? `Report (${results.length})` : 'Report'}
                  onClick={() => setView('report')}
                  isDisabled={!hasRun}
                  variant={view === 'report' ? 'contained' : 'subtle'}
                  type='button'
                  buttonSize='30px'
                  buttonRadius='8px'
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {isRunning ? (
                  <Button2
                    text={isStopping ? 'Stopping…' : 'Stop'}
                    leftElement={<Icon iconName='SubtractCircle' size={14} color='var(--tma-danger-text)' />}
                    onClick={handleStop}
                    isDisabled={isStopping}
                    variant='outlined'
                    isDanger
                    type='button'
                    buttonSize='30px'
                    buttonRadius='8px'
                  />
                ) : null}
                <Button2
                  text={
                    isRunning
                      ? `Running ${processed}/${runTotal}…`
                      : selectedTenants.length === 0
                        ? // A disabled "Verify 0 tenants" states a fact; it does not say what
                          // to do about it. Name the next action instead.
                          'Select tenants to start'
                        : mode === 'apply'
                          ? `Migrate ${selectedTenants.length} tenant${selectedTenants.length !== 1 ? 's' : ''}`
                          : `Verify ${selectedTenants.length} tenant${selectedTenants.length !== 1 ? 's' : ''}`
                  }
                  leftElement={
                    <Icon
                      iconName={mode === 'apply' ? 'Swap' : 'Search'}
                      size={14}
                      color={selectedTenants.length === 0 || isRunning ? 'var(--tma-text-faint)' : '#ffffff'}
                    />
                  }
                  onClick={handleRun}
                  isDisabled={selectedTenants.length === 0 || isRunning}
                  variant='contained'
                  isDanger={mode === 'apply'}
                  type='button'
                  buttonSize='30px'
                  buttonRadius='8px'
                />
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--tma-list-card-border)', margin: '0 -20px' }} />
          </div>

          {view === 'tenants' ? (
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 0' }} className='custom-scrollbar'>
              {isLoadingTenants ? (
                <LoadingState text={`Loading tenants for ${selectedSegment}…`} />
              ) : tenants.length === 0 ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <EmptyState
                    imageName='emptyfolder'
                    size='medium'
                    title='No tenants found'
                    text={`No tenants exist in ${selectedSegment}.`}
                  />
                </div>
              ) : filteredTenants.length === 0 ? (
                <Text align='center' textColor='grey'>
                  No tenants match '{searchQuery}'
                </Text>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '8px' }}>
                  {filteredTenants.map((tenant) => {
                    const isSelected = selectedTenants.includes(tenant.id)
                    return (
                      <div
                        key={tenant.id}
                        style={{
                          borderStyle: 'solid',
                          borderWidth: '1px',
                          borderColor: isSelected ? 'var(--tma-accent)' : 'var(--tma-list-card-border)',
                          borderRadius: '6px',
                          background: 'var(--tma-panel-bg)',
                          padding: '8px 12px',
                          transition: 'border-color 0.15s ease'
                        }}
                      >
                        <Checkbox
                          name={tenant.id}
                          label={tenant.displayName}
                          description={tenant.tenantName}
                          isChecked={isSelected}
                          isDisabled={isRunning}
                          onClick={() => toggleTenant(tenant.id)}
                          value='a'
                          isLabelSingleLine
                          isDescriptionSingleLine
                          innerSpacing={0}
                        />
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          ) : (
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 0' }} className='custom-scrollbar'>
              {/* Progress / summary panel. */}
              <div
                style={{
                  flexShrink: 0,
                  border: '1px solid var(--tma-list-card-border)',
                  borderRadius: '8px',
                  background: 'var(--tma-card-bg)',
                  padding: '12px 14px',
                  marginBottom: '16px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                  <Text
                    textStyle='h6'
                    content={isApplyRun ? `Migrating ${selectedSegmentLabel}` : `Verifying ${selectedSegmentLabel}`}
                    style={{ fontSize: '14px' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Button2
                      text='Export CSV'
                      leftElement={<Icon iconName='OfficeFileDoc1' size={14} />}
                      onClick={exportCsv}
                      isDisabled={results.length === 0}
                      variant='outlined'
                      type='button'
                      buttonSize='30px'
                      buttonRadius='8px'
                    />
                    <Button2
                      text='Export JSON'
                      leftElement={<Icon iconName='OfficeFileDoc1' size={14} />}
                      onClick={exportJson}
                      isDisabled={results.length === 0}
                      variant='outlined'
                      type='button'
                      buttonSize='30px'
                      buttonRadius='8px'
                    />
                  </div>
                </div>

                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    height: '20px',
                    borderRadius: '999px',
                    background: 'var(--tma-active-bg)',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      width: `${progressPercentage}%`,
                      flexShrink: 0,
                      background: failedResults.length > 0 ? 'var(--tma-warning-text)' : 'var(--tma-accent)',
                      transition: 'width 0.3s ease'
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#ffffff',
                      textShadow: '0 0 3px rgba(0,0,0,0.55)'
                    }}
                  >
                    {`${processed} / ${runTotal} tenants`}
                  </span>
                </div>

                <Text
                  textStyle='description'
                  textColor='grey'
                  content={
                    isRunning
                      ? `${currentTenantName ? `Now: ${currentTenantName}` : 'Starting…'}${
                          remainingMs > 0 ? ` · about ${formatDuration(remainingMs)} left` : ''
                        }${isStopping ? ' · stopping now, the current request is being aborted' : ''}`
                      : wasStopped
                        ? `Stopped by operator after ${processed} of ${runTotal} tenants. The results below are everything that ran.`
                        : `Finished ${processed} tenant${processed !== 1 ? 's' : ''} in ${formatDuration(elapsedMs)}.`
                  }
                  style={{ display: 'block', marginTop: '8px' }}
                />

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                  <span style={pill(OUTCOME_COLOR.failed)}>Failed {failedResults.length}</span>
                  {stoppedResults.length > 0 ? <span style={pill(OUTCOME_COLOR.stopped)}>Stopped {stoppedResults.length}</span> : null}
                  <span style={pill(OUTCOME_COLOR.skipped)}>Skipped {skippedResults.length}</span>
                  <span style={pill(CHANGED_COLOR)}>
                    {isApplyRun ? `Migrated ${changedResults.length}` : `To migrate ${changedResults.length}`}
                  </span>
                  <span style={pill(OUTCOME_COLOR.ok)}>Up to date {unchangedResults.length}</span>
                  <span style={pill('var(--tma-text-muted)')}>
                    {isApplyRun ? `${totalInserted} keys inserted` : `${totalInserted} keys to insert`}
                  </span>
                </div>

                {isFullyMigrated && (
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'flex-start',
                      marginTop: '10px',
                      border: '1px solid color-mix(in srgb, var(--tma-success-text) 35%, var(--tma-panel-bg))',
                      background: 'var(--tma-success-tint)',
                      borderRadius: '8px',
                      padding: '10px 12px'
                    }}
                  >
                    <Icon iconName='CheckSquare' size={16} color='var(--tma-success-text)' style={{ flexShrink: 0, marginTop: '1px' }} />
                    <Text
                      textStyle='description'
                      content={`Nothing left to migrate: all ${results.length} tenants reported 0 keys to insert, with no failures and no skips.`}
                      style={{ color: 'var(--tma-success-text)' }}
                    />
                  </div>
                )}
              </div>

              {/* Failures first - the only rows that need someone to act. */}
              {results.length === 0 && !isRunning ? (
                <div style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <EmptyState imageName='emptyfolder' size='medium' title='No results yet' text='Select tenants and run a verify pass.' />
                </div>
              ) : (
                <>
                  {renderResultSection('Failed', OUTCOME_COLOR.failed, failedResults, 'The request threw, or keys could not be written.')}
                  {renderResultSection(
                    'Stopped',
                    OUTCOME_COLOR.stopped,
                    stoppedResults,
                    'The request was aborted mid-flight when you pressed Stop. Re-run to see where this tenant stands.'
                  )}
                  {renderResultSection(
                    'Skipped',
                    OUTCOME_COLOR.skipped,
                    skippedResults,
                    'No vault could be read, so nothing was attempted for these tenants.'
                  )}
                  {renderResultSection(
                    isApplyRun ? 'Migrated' : 'To migrate',
                    CHANGED_COLOR,
                    changedResults,
                    isApplyRun ? 'Keys were inserted into SQL.' : 'A migrate run would insert keys for these tenants.'
                  )}
                  {renderResultSection(
                    'Up to date',
                    UP_TO_DATE_COLOR,
                    unchangedResults,
                    'SQL already holds every key the vault has. A non-zero "Not set in vault" is still fine here: the tenant simply never had that setting.'
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Migrate confirmation - verify never reaches this. */}
      <Modal
        isOpen={isConfirmModalOpen}
        width='45%'
        onClose={() => setIsConfirmModalOpen(false)}
        onCancel={() => setIsConfirmModalOpen(false)}
        hasHeaderBorder={true}
        headerTitle={`Migrate tenant configuration: ${selectedSegmentLabel}`}
        height='auto'
        onConfirm={() => {
          setIsConfirmModalOpen(false)
          runMigration('apply')
        }}
        variant='confirm'
        labelConfirm={`Migrate ${selectedTenants.length} tenant${selectedTenants.length !== 1 ? 's' : ''}`}
        labelCancel='Cancel'
      >
        <Container isVertical spacing={20}>
          <Text>
            This <strong>writes</strong> to the encrypted SQL configuration table for the following{' '}
            <strong>{selectedTenants.length}</strong> tenant{selectedTenants.length !== 1 ? 's' : ''} in{' '}
            <strong>{selectedSegmentLabel}</strong>:
          </Text>

          <div
            style={{
              maxHeight: '260px',
              overflowY: 'auto',
              border: '1px solid var(--tma-list-card-border)',
              borderRadius: '6px',
              padding: '10px 12px'
            }}
          >
            <Container isVertical spacing={6}>
              {selectedTenantDetails.map((tenant) => (
                <Text key={tenant.id}>
                  • {tenant.displayName} ({tenant.tenantName})
                </Text>
              ))}
            </Container>
          </div>

          <Text textColor='grey'>
            Tenants are processed one at a time (about 5-10 seconds each) and existing SQL rows are never overwritten, so the run is safe to
            stop and re-run.
          </Text>
        </Container>
      </Modal>

      {/* Bulk select by pasted name list. */}
      <Modal
        isOpen={isBulkSelectModalOpen}
        width='50%'
        onClose={closeBulkSelect}
        onCancel={closeBulkSelect}
        hasHeaderBorder={true}
        headerTitle={`Bulk Select Tenants: ${selectedSegmentLabel}`}
        height='auto'
        onConfirm={confirmBulkSelection}
        variant='confirm'
        labelConfirm={`Select ${uniqueFoundTenantIds.length} Tenant${uniqueFoundTenantIds.length !== 1 ? 's' : ''}`}
        labelCancel='Cancel'
        isConfirmDisabled={uniqueFoundTenantIds.length === 0 || hasAmbiguousNames}
      >
        <Container isVertical spacing={20}>
          {/* Names only match tenants in the active stage tab, so state that up front. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
              border: '1px solid var(--tma-list-card-border)',
              borderRadius: '6px',
              background: 'var(--tma-page-bg)',
              padding: '8px 12px'
            }}
          >
            <span
              style={{
                display: 'inline-block',
                background: 'var(--tma-accent)',
                color: '#ffffff',
                borderRadius: '10px',
                padding: '2px 10px',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.4px'
              }}
            >
              {selectedSegmentLabel}
            </span>
            <Text textStyle='description' textColor='grey'>
              {`Matching against the ${tenants.length} tenant${tenants.length !== 1 ? 's' : ''} in ${selectedSegmentLabel}. Switch the stage tab to select tenants from another stage.`}
            </Text>
          </div>

          <Text>Enter tenant names or display names, separated by commas or new lines:</Text>

          <TextFieldWrapper labelText='Tenant Names' style={{ width: '100%' }} spacing={0}>
            <textarea
              value={bulkSelectInput}
              onChange={(e) => {
                const newValue = e.target.value
                const oldValue = bulkSelectInput
                setBulkSelectInput(newValue)

                if (debounceTimer) {
                  clearTimeout(debounceTimer)
                }

                if (newValue.trim() === '') {
                  processBulkSelection(newValue)
                  return
                }

                // A paste lands as one big change; match it near-instantly instead of
                // waiting out the type-ahead debounce. Both timers are stored the same
                // way so the cleanup effect above clears whichever one is pending when
                // the modal closes or the page unmounts.
                const lengthDiff = Math.abs(newValue.length - oldValue.length)
                const newTimer = setTimeout(() => processBulkSelection(newValue), lengthDiff > 10 ? 50 : 300)
                setDebounceTimer(newTimer)
              }}
              placeholder='Example:&#10;tenant-name-1&#10;tenant-name-2&#10;tenant-name-3'
              style={{
                width: '100%',
                height: '120px',
                padding: '8px',
                border: '1px solid var(--tma-card-border)',
                borderRadius: '4px',
                fontFamily: 'inherit',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </TextFieldWrapper>

          {hasAmbiguousNames && (
            <div
              style={{
                border: '1px solid var(--tma-warning-text)',
                borderRadius: '6px',
                background: 'var(--tma-warning-tint)',
                padding: '10px 12px'
              }}
            >
              <Text style={{ fontWeight: 'bold', color: 'var(--tma-warning-text)' }}>
                {bulkSelectResults.ambiguous.length} name{bulkSelectResults.ambiguous.length !== 1 ? 's' : ''} match more than one tenant.
                Make {bulkSelectResults.ambiguous.length !== 1 ? 'them' : 'it'} unique before selecting.
              </Text>
              {bulkSelectResults.ambiguous.map((entry, index) => (
                <Text
                  key={`${entry.input}-${index}`}
                  style={{ marginLeft: '8px', marginTop: '4px', color: 'var(--tma-warning-text)', fontSize: '12px' }}
                >
                  • '{entry.input}' → {entry.candidates.map((candidate) => candidate.displayName).join(', ')}
                </Text>
              ))}
            </div>
          )}

          {(bulkSelectResults.found.length > 0 || hasAmbiguousNames || bulkSelectResults.notFound.length > 0) && (
            <>
              <Text textColor='grey'>
                Parsed {bulkSelectResults.found.length + bulkSelectResults.ambiguous.length + bulkSelectResults.notFound.length} name
                {bulkSelectResults.found.length + bulkSelectResults.ambiguous.length + bulkSelectResults.notFound.length !== 1 ? 's' : ''}:
                found {bulkSelectResults.found.length}, ambiguous {bulkSelectResults.ambiguous.length}, not found{' '}
                {bulkSelectResults.notFound.length}.
              </Text>
              <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
                <div style={{ width: '50%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text style={{ fontWeight: 'bold', color: 'green' }}>
                    ✓ Found in {selectedSegmentLabel} ({bulkSelectResults.found.length})
                  </Text>
                  <div
                    style={{
                      height: '150px',
                      overflowY: 'scroll',
                      border: '1px solid var(--tma-card-border)',
                      borderRadius: '4px',
                      padding: '8px',
                      backgroundColor: 'color-mix(in srgb, var(--tma-accent) 8%, var(--tma-panel-bg))'
                    }}
                  >
                    {bulkSelectResults.found.length > 0 ? (
                      bulkSelectResults.found.map((match, index) => {
                        const tenant = tenants.find((t) => t.id === match.tenantId)
                        return tenant ? (
                          <div key={`${match.input}-${match.tenantId}-${index}`} style={{ marginLeft: '8px', marginBottom: '4px' }}>
                            <Text style={{ color: 'green' }}>
                              • {tenant.displayName} ({tenant.tenantName})
                            </Text>
                            {!match.isExact && (
                              <Text style={{ marginLeft: '10px', color: 'var(--tma-success-text)', fontSize: '12px' }}>
                                matched on '{match.input}'
                              </Text>
                            )}
                          </div>
                        ) : null
                      })
                    ) : (
                      <Text style={{ color: 'var(--tma-text-muted)', fontStyle: 'italic' }}>No tenants found yet</Text>
                    )}
                  </div>
                </div>

                <div style={{ width: '50%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text style={{ fontWeight: 'bold', color: 'red' }}>
                    ✗ Not found in {selectedSegmentLabel} ({bulkSelectResults.notFound.length})
                  </Text>
                  <div
                    style={{
                      height: '150px',
                      overflowY: 'scroll',
                      border: '1px solid var(--tma-card-border)',
                      borderRadius: '4px',
                      padding: '8px',
                      backgroundColor: 'var(--tma-danger-tint)'
                    }}
                  >
                    {bulkSelectResults.notFound.length > 0 ? (
                      bulkSelectResults.notFound.map((name) => (
                        <Text key={name} style={{ marginLeft: '8px', color: 'red', marginBottom: '4px' }}>
                          • {name}
                        </Text>
                      ))
                    ) : (
                      <Text style={{ color: 'var(--tma-text-muted)', fontStyle: 'italic' }}>All tenants found</Text>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </Container>
      </Modal>
    </>
  )
}

export default TenantConfigMigration
